import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { Send, UploadFile } from "@mui/icons-material";
import { toast } from "react-toastify";
import baseURL from "../../config";
import DashboardLayout from "../DashboardLayout";
import PageLoader from "../../../../Reusable/PageLoader";

// Custom COLORS for a Microsoft-like aesthetic
const MS_COLORS = {
  primary: '#2D4A3D',       // Microsoft's signature blue
  primaryLight: '#EBF5EE',  // Lighter shade for active background
  text: '#323130',          // Dark gray for main text
  textSecondary: '#605D5C', // Lighter gray for secondary text
  background: '#FFFFFF',    // Pure white background
  backgroundLight: '#F3F2F1', // Light gray for subtle accents
  border: '#E1E1E1',        // Light gray for borders
  hoverBackground: '#F0F0F0', // Very light gray on hover
  white: '#FFFFFF',
  accent: '#107C10',        // A green for actions/completed
  red: '#D83B01',           // Microsoft's red for errors/closed
};

const NewsletterManagement = () => {
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [file, setFile] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [subscriberCount, setSubscriberCount] = useState(null); // Use null for initial loading state
  const [isLoading, setIsLoading] = useState(true); // New state to manage initial loading

  const isSmallScreen = useMediaQuery("(max-width:600px)");

  // Fetches the total number of newsletter subscribers from the backend
  const fetchSubscriberCount = async () => {
    setIsLoading(true);
    try {
       // await new Promise(resolve => setTimeout(resolve, 4000));

      const response = await fetch(`${baseURL}/admin/subscribers/count`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setSubscriberCount(data.count);
    } catch (error) {
      console.error("Failed to fetch subscriber count:", error);
      toast.error("Failed to load subscriber count.");
      setSubscriberCount('—');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriberCount();
  }, []);

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const handleSendNewsletter = async () => {
    if (!subject.trim() || !content.trim()) {
      toast.error("Subject and content cannot be empty.");
      return;
    }

    setIsSending(true);

    const formData = new FormData();
    formData.append("subject", subject);
    formData.append("content", content);
    if (file) {
      formData.append("attachment", file);
    }

    try {
      const response = await fetch(`${baseURL}/admin/newsletter/send`, {
        method: "POST",
        body: formData,
        credentials: 'include',
      });

      if (response.ok) {
        toast.success("Newsletter sent successfully to all subscribers!");
        setSubject("");
        setContent("");
        setFile(null);
        // Refresh the subscriber count in case new users subscribed
        fetchSubscriberCount();
      } else {
        const errorData = await response.json();
        console.error("Failed to send newsletter:", errorData);
        toast.error(errorData.message || "Failed to send newsletter.");
      }
    } catch (error) {
      console.error("Error sending newsletter:", error);
      toast.error("An error occurred while sending the newsletter. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <PageLoader message="Loading subscriber data..." />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
    
      <Typography
        variant="h6"
        sx={{
          color: MS_COLORS.text,
          mb: 3,
          fontWeight: "bold",
          fontSize: isSmallScreen ? "1.1rem" : "1.4rem",
        }}
      >
        Newsletter Management
      </Typography>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "left",
          mb: 3,
          flexDirection: isSmallScreen ? "column" : "row",
        }}
      >
        <Typography
          variant="body1"
          sx={{
            color: MS_COLORS.textSecondary,
            fontWeight: "bold",
            fontSize: "1rem",
            mb: isSmallScreen ? 2 : 0,
          }}
        >
          Total Subscribers:{" "}
          <Typography
            component="span"
            sx={{
              color: MS_COLORS.primary,
              fontWeight: "bold",
              fontSize: "1.2rem",
            }}
          >
            {subscriberCount}
          </Typography>
        </Typography>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          p: 3,
          mb: 3,
          borderColor: MS_COLORS.border,
          boxShadow: "none",
          borderRadius: 1,
        }}
      >
        <Typography variant="h6" sx={{ color: MS_COLORS.text, mb: 2, fontWeight: "600" }}>
          Compose New Newsletter
        </Typography>

        <TextField
          fullWidth
          label="Subject"
          variant="outlined"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          sx={{ mb: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: "4px",
              "& fieldset": { borderColor: MS_COLORS.border },
              "&:hover fieldset": { borderColor: MS_COLORS.primary },
              "&.Mui-focused fieldset": { borderColor: MS_COLORS.primary, borderWidth: 2 },
            },
            "& .MuiInputBase-input": { fontSize: "0.9rem", color: MS_COLORS.text },
            "& .MuiInputLabel-root": { fontSize: "0.9rem", color: MS_COLORS.textSecondary },
           }}
        />

        <TextField
          fullWidth
          multiline
          minRows={10}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          variant="outlined"
          label="Newsletter Content"
          placeholder="Type your newsletter content here..."
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: "4px",
              "& fieldset": { borderColor: MS_COLORS.border },
              "&:hover fieldset": { borderColor: MS_COLORS.primary },
              "&.Mui-focused fieldset": { borderColor: MS_COLORS.primary, borderWidth: 2 },
            },
            "& .MuiInputBase-input": { fontSize: "0.9rem", color: MS_COLORS.text },
            "& .MuiInputLabel-root": { fontSize: "0.9rem", color: MS_COLORS.textSecondary },
          }}
        />
        
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 3 }}>
          <Button
            component="label"
            variant="outlined"
            startIcon={<UploadFile />}
            sx={{
              textTransform: "none",
              fontSize: "0.85rem",
              borderColor: MS_COLORS.border,
              color: MS_COLORS.textSecondary,
              "&:hover": {
                borderColor: MS_COLORS.primary,
                backgroundColor: MS_COLORS.primaryLight,
              },
            }}
          >
            {file ? "Change File" : "Upload Attachment"}
            <input type="file" hidden onChange={handleFileChange} />
          </Button>

          {file && (
            <Typography variant="body2" sx={{ color: MS_COLORS.textSecondary }}>
              Selected file: **{file.name}**
            </Typography>
          )}
        </Box>

        <Button
          variant="contained"
          color="primary"
          onClick={handleSendNewsletter}
          disabled={isSending || !subject || !content}
          startIcon={isSending ? null : <Send />}
          sx={{
            textTransform: "none",
            fontSize: "1rem",
            py: 1,
            px: 3,
            backgroundColor: MS_COLORS.primary,
            boxShadow: 0,
            "&:hover": { backgroundColor: MS_COLORS.primary, opacity: 0.9, boxShadow: 0 },
            "&:disabled": { backgroundColor: MS_COLORS.border, color: MS_COLORS.textSecondary },
          }}
        >
          {isSending ? (
            <>
              <CircularProgress size={20} sx={{ color: MS_COLORS.white, mr: 1 }} />
              Sending...
            </>
          ) : (
            "Send Newsletter"
          )}
        </Button>
      </Paper>
    </DashboardLayout>
  );
};

export default NewsletterManagement;